import {
  useCreateTransactionApprovalMatrixMutation,
  useFetchTransactionApprovalMatrixQuery,
  useUpdateTransactionApprovalMatrixMutation,
  useUserOptionsQry
} from 'mmfintech-backend-api'
import type { ApprovalLevelRequest, ApprovalMatrixRequest } from 'mmfintech-backend-api/types'
import { tr } from 'mmfintech-commons'
import { BaseApprovalMatrixModal } from '../BaseApprovalMatrixModal'
import TransactionApprovalMatrixTooltip from './TransactionApprovalMatrixTooltip/TransactionApprovalMatrixTooltip'

import './TransactionApprovalMatrixModal.scss'

type ApproversData = {
  id: string | number
  amountThreshold?: number | string
  priority: number
  requiredApprovals: number
  approvers: number[]
  isThresholdEnabled?: boolean
}

export const TRANSACTION_APPROVAL_MATRIX_MODAL_MODE = {
  CREATE: 'create',
  EDIT: 'edit'
} as const

type TransactionApprovalMatrixModalMode =
  (typeof TRANSACTION_APPROVAL_MATRIX_MODAL_MODE)[keyof typeof TRANSACTION_APPROVAL_MATRIX_MODAL_MODE]

interface Props {
  modalMode: TransactionApprovalMatrixModalMode
}

const TransactionApprovalMatrixModal = ({ modalMode }: Props) => {
  const { data: matrix, isLoading: matrixLoading } = useFetchTransactionApprovalMatrixQuery()

  const [updateMatrix, { isLoading: isUpdating, error: updateError }] = useUpdateTransactionApprovalMatrixMutation()
  const [createMatrix, { isLoading: isCreating, error: createError }] = useCreateTransactionApprovalMatrixMutation()

  const { users, usersError, usersFetching } = useUserOptionsQry(1000, ['OWNER', 'ADMINISTRATOR'], 'WRITER')

  const transformApproversToPayload = (approversData: ApproversData[]): ApprovalMatrixRequest => {
    const approvalLevels: ApprovalLevelRequest[] = approversData
      .map(({ id, isThresholdEnabled, amountThreshold, ...approver }) => {
        if (typeof id === 'number') {
          return {
            id,
            ...(isThresholdEnabled && { amountThreshold: Number(amountThreshold) }),
            ...approver
          }
        } else {
          return {
            ...(isThresholdEnabled && { amountThreshold: Number(amountThreshold) }),
            ...approver
          }
        }
      })
      .map(approver => ({
        ...approver,
        approvers: approver.approvers.map((approverId: number) => ({
          merchantUserId: approverId,
          approvalWeight: 1
        }))
      }))

    return { approvalLevels }
  }

  const handleCreateMatrix = async (payload: ApprovalMatrixRequest) => {
    return await createMatrix(payload).unwrap()
  }

  const handleUpdateMatrix = async (payload: ApprovalMatrixRequest) => {
    return await updateMatrix(payload).unwrap()
  }

  const getSuccessMessages = (): { title: string; subtitle: string } => {
    let title: string
    const subtitle: string = tr(
      'FRONTEND.TRANSACTION_MATRIX.SUCCESS_CREATE_SUBTITLE',
      'You can now manage it under Transaction Approval Matrix'
    )

    if (modalMode === TRANSACTION_APPROVAL_MATRIX_MODAL_MODE.CREATE) {
      title = tr('FRONTEND.TRANSACTION_MATRIX.SUCCESS_CREATE_TITLE', 'Transaction approval matrix created successfully')
    } else {
      title = tr('FRONTEND.TRANSACTION_MATRIX.SUCCESS_UPDATE_TITLE', 'Matrix successfully updated')
    }

    return { title, subtitle }
  }

  return (
    <div className='transaction-approval-matrix-modal'>
      {modalMode === TRANSACTION_APPROVAL_MATRIX_MODAL_MODE.CREATE && (
        <div className='transaction-approval-matrix-description'>
          <p>
            {tr(
              'FRONTEND.TRANSACTION_MATRIX.DESCRIPTION',
              'Set up an approval process for outgoing transactions. This ensures that payments, transfers, or currency exchanges require approval before being processed. Approvers can be assigned based on transaction limits too.'
            )}
          </p>
        </div>
      )}

      <BaseApprovalMatrixModal
        matrix={modalMode === TRANSACTION_APPROVAL_MATRIX_MODAL_MODE.EDIT ? matrix : undefined}
        users={users}
        usersFetching={usersFetching || matrixLoading}
        transformApproversToPayload={transformApproversToPayload}
        createMutation={handleCreateMatrix}
        updateMutation={handleUpdateMatrix}
        isCreating={isCreating}
        isUpdating={isUpdating}
        errors={[usersError, createError, updateError]}
        approversTooltip={<TransactionApprovalMatrixTooltip />}
        selectUserDropdownHint={
          <span>
            {tr(
              'FRONTEND.APPROVAL_MATRIX.DROPDOWN_HINT_PREFIX',
              'Only users with Global Send permission can be added.'
            )}
          </span>
        }
        successMessage={getSuccessMessages()}
      />
    </div>
  )
}

export default TransactionApprovalMatrixModal
